import type { EquipmentAccessorial } from '@fv/client-types'
import type { Workflow } from '@fv/models'
import type {
  EquipmentAccessorialType,
  EquipmentType,
} from '@fv/models/equipment'

import { useTruckloadEquipmentOptions } from '@/hooks/useEquipmentOptions'

import type { ShipmentEquipmentFormModel } from './types'

export function composeEquipmentAccessorialsDescription(
  getAccessorialName: (k: EquipmentAccessorialType) => string,
  accessorials?: EquipmentAccessorial[],
) {
  if (!accessorials?.length) return 'None Selected'

  const countHandler = (a: EquipmentAccessorial) =>
    `${'count' in a ? a.count + ' ' : ''}${getAccessorialName(a.key)}`

  const composerDict: {
    [key: string]: (a: EquipmentAccessorial) => string
  } = {
    'load-bars': countHandler,
    chains: countHandler,
    straps: countHandler,
    tarps: a =>
      `${'size' in a ? a.size + ' ft. ' : ''}${getAccessorialName(a.key)}`,
    temp: a => {
      if ('min' in a && 'max' in a && !!a.uom) {
        return `Temp between ${a.min} and ${a.max} \u00b0${a.uom.toUpperCase()}`
      }

      return 'Temp control'
    },
  }

  return accessorials
    .map(a => {
      return composerDict[a.key]
        ? composerDict[a.key](a)
        : getAccessorialName(a.key)
    })
    .join(' / ')
}

const DEFAULT_TRUCKLOAD_EQUIPMENT_TYPE: EquipmentType = 'dry-van-53'

export const defaultShipmentEquipmentFormModel = (
  workflow: Workflow,
  truckloadEquipmentType?: EquipmentType,
): ShipmentEquipmentFormModel => ({
  type:
    workflow === 'truckload'
      ? (truckloadEquipmentType ?? DEFAULT_TRUCKLOAD_EQUIPMENT_TYPE)
      : workflow,
  accessorials: [],
  alternateTypes: [],
})

export const useDefaultShipmentEquipmentFormModel = () => {
  const { options } = useTruckloadEquipmentOptions()

  return (workflow: Workflow): ShipmentEquipmentFormModel =>
    defaultShipmentEquipmentFormModel(workflow, options[0]?.key)
}
